import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'
import type {
  ApiFieldDef,
  ApiLot,
  ApiMaterial,
  ApiProfile,
  FieldDataType,
  FieldValidation,
  InventoryProfileType,
  MaterialListResult,
  MaterialStatus,
  MaterialStockFilter,
  PendingRow,
  ProfileFieldEntry,
  ReorderCounts,
  ReorderFilter,
  ReorderListResult,
} from '@/types/inventory'

export type AddLotPayload = {
  /**
   * The manufacturer's number printed on the container. Optional.
   *
   * Our own `LOT-<yy>-<nnnn>` is always minted by the server and cannot be
   * supplied: it is what audit rows, pick sheets and storage positions refer
   * to, while this is the handle a supplier recall is actioned through. Two
   * identifiers, two owners — storing one in place of the other made recall
   * matching depend on how the stock happened to be received.
   */
  vendorLotNumber?: string
  quantity: number
  /**
   * Required on a receipt: the date is printed on the container being received,
   * and stock recorded without one cannot be reasoned about by any FEFO pick,
   * expiry filter or shelf-life check. The backend rejects a receipt without it.
   */
  expiryDate: string
  note?: string
  /** @deprecated Superseded by `storageNodeId`; kept while StorageZone is retired. */
  zoneId?: string
  /** @deprecated Superseded by `storageNodeId` — the position IS the location. */
  locationCode?: string
  /**
   * The storage-hierarchy POSITION this lot sits in. The server validates it is
   * in-tenant, a leaf, and not blocked. Prefer this over zoneId/locationCode:
   * without it, received stock never enters the hierarchy.
   */
  storageNodeId?: string
}

/** Partial lot edit. `null` clears a field; omitting it leaves the field alone. */
export type UpdateLotPayload = {
  lotNumber?: string
  quantity?: number
  expiryDate?: string | null
  note?: string | null
  /** @deprecated Superseded by `storageNodeId`. */
  zoneId?: string | null
  /** @deprecated Superseded by `storageNodeId`. */
  locationCode?: string | null
  /** Move the lot to a POSITION, or null to un-home it. */
  storageNodeId?: string | null
}

// ─── Request payload shapes (mirror cgt-backend inventory DTOs) ─────────────

/** Body for `POST /inventory/materials`. */
export type CreateMaterialPayload = {
  /**
   * The MAT-###### key. Minted by the server when omitted, which is what the
   * app always does — only an importer preserving an existing number sends one.
   */
  materialId?: string
  materialCode: string
  materialName: string
  materialType: string
  status?: MaterialStatus
  /** Product codes live on the material, not a shared profile. */
  primaryProductCode?: string
  productCodes?: string[]
  manufacturerProfileId?: string
  storageProfileId?: string
  traceabilityProfileId?: string
  qualityProfileId?: string
  regulatoryProfileId?: string
  materialDescriptionProfileId?: string
  inventoryUnitProfileId?: string
  productCodeProfileId?: string
  shelfLifeProfileId?: string
  usageProfileId?: string
  costProfileId?: string
  attachmentsProfileId?: string
  /** Set of row actions; empty string clears it back to the Default set. */
  actionsProfileId?: string
  attributes?: Record<string, ProfileFieldEntry>
}

/**
 * One still-open reservation against a lot — a row of the breakdown behind the
 * lot's In Use figure.
 *
 * The patient context is null when the holding submission no longer exists:
 * `submissionId` is a soft ref, and the hold outlives it. Such a row is still
 * holding stock, so it is shown rather than dropped.
 */
export interface ApiLotHold {
  submissionId: string
  fieldId: string
  requirementId: string
  qty: number
  /** ISO instant the hold was taken. */
  createdAt: string
  /** The holding step's status, or null when its execution row has gone. */
  stepStatus: string | null
  /**
   * Whether work has actually begun on the holding step. What the detail dialog
   * filters on, so its rows total the column that opened it.
   */
  started: boolean
  patientMrn: string | null
  formType: string | null
  submissionStatus: string | null
}

/** Body for `PATCH /inventory/materials/:id`. */
export type UpdateMaterialPayload = Partial<CreateMaterialPayload>

/** Body for `POST /inventory/profiles`. */
export type CreateProfilePayload = {
  profileType: InventoryProfileType
  name: string
  description?: string
  fields?: Record<string, ProfileFieldEntry>
  isDefault?: boolean
}

/** Body for `PATCH /inventory/profiles/:id`. */
export type UpdateProfilePayload = Partial<Omit<CreateProfilePayload, 'profileType'>>

/** Body for `POST /inventory/field-defs`. */
export type CreateFieldDefPayload = {
  profileType: InventoryProfileType
  key: string
  label: string
  description?: string
  dataType?: FieldDataType
  order?: number
  activeByDefault?: boolean
  options?: string[]
  validation?: FieldValidation | null
}

/** Body for `PATCH /inventory/field-defs/:id`. */
export type UpdateFieldDefPayload = Partial<Omit<CreateFieldDefPayload, 'profileType' | 'key'>>

/**
 * What `GET /inventory/materials/lots/resolve` answers with: the lot, plus the
 * material identity flattened onto it. A scan asks "what is this container", so
 * the two halves of the code come back as one record rather than a nested one.
 */
export type ApiResolvedLot = {
  id: string
  lotNumber: string
  vendorLotNumber: string | null
  quantity: number
  expiryDate: string | null
  gateStatus: 'quarantine' | 'in_process' | 'available' | null
  storageNodeId: string | null
  locationCode: string | null
  materialId: string
  materialCode: string
  materialName: string
  materialType: string
}

/** Shared fetch wrapper — bearer token + `X-Tenant-Id` header (equipmentApi pattern). */
async function request<T>(path: string, tenantId: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders({
      'Content-Type': 'application/json',
      'X-Tenant-Id': tenantId,
      ...init?.headers,
    }),
  })
  handleUnauthorized(res.status)
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { message?: string | string[] } | null
    const message = Array.isArray(body?.message) ? body.message.join('; ') : body?.message
    // The status rides along on the Error. Callers that only render `.message`
    // are unaffected; the ones that must tell 403 from 404 — the scan lookup —
    // read it through `statusOf` in `@/lib/httpError`, which already
    // duck-types this exact shape. See that module on the two dialects this
    // codebase throws in.
    throw Object.assign(new Error(message || `Request failed: ${res.status}`), {
      status: res.status,
    })
  }
  return res.json() as Promise<T>
}

// ─── Bulk import (mirrors cgt-backend src/inventory/import) ────────────────

/** What one spreadsheet column writes to. */
export type ImportTarget =
  | { kind: 'materialScalar'; key: string }
  | { kind: 'profileName'; profileType: InventoryProfileType }
  | { kind: 'fieldValue'; profileType: InventoryProfileType; key: string }

export interface ImportSchemaScalar {
  key: string
  label: string
  dataType: FieldDataType
  options?: string[]
  multiValue?: boolean
  required?: boolean
}

export interface ImportSchemaCategory {
  profileType: InventoryProfileType
  label: string
  fkField: string | null
  /** A named profile can be assigned for this category. */
  assignable: boolean
  /**
   * An import may write this category. False for `actions` and `status`, whose
   * defs are booleans switching UI features on and off rather than describing a
   * material, and for the retired `product_code`.
   */
  importable: boolean
  /** Withdrawn from the authoring surface; reported so nothing existing is hidden. */
  retired: boolean
  fields: ApiFieldDef[]
  profiles: { id: string; name: string; isDefault: boolean }[]
}

export interface ImportSchema {
  materialScalars: ImportSchemaScalar[]
  categories: ImportSchemaCategory[]
}

export type ResolutionStatus = 'resolved' | 'ambiguous' | 'unmatched' | 'ignored'

export interface HeaderResolution {
  header: string
  index: number
  status: ResolutionStatus
  target?: ImportTarget
  candidates?: ImportTarget[]
  note?: string
}

export type CategoryOutcome =
  | { kind: 'named'; profileId: string; profileName: string }
  | { kind: 'undecided'; valueSetKey: string; values: Record<string, ProfileFieldEntry> }
  | { kind: 'attributesOnly'; values: Record<string, ProfileFieldEntry> }

export interface ImportRowPlan {
  /** 1-based and PHYSICAL: the header is row 1, so the first data row is row 2. */
  row: number
  materialCode: string | null
  action: 'create' | 'update'
  existingId: string | null
  scalars: Record<string, unknown>
  attributes: Record<string, ProfileFieldEntry>
  categories: Partial<Record<InventoryProfileType, CategoryOutcome>>
  /** Problems belonging to this row — a missing required value, a repeated code. */
  errors: string[]
  /**
   * Problems belonging to a COLUMN that land on this row. Stated once each in
   * `ImportPlan.errors`; the row list shows only how many it has, because a
   * mis-mapped column repeated down every row buries the row-specific ones.
   */
  columnIssues: string[]
}

/** A set of values that appeared in the file with no profile to put it on yet. */
export interface ImportProfileDecision {
  profileType: InventoryProfileType
  categoryLabel: string
  valueSetKey: string
  values: Record<string, ProfileFieldEntry>
  rows: number[]
  suggestedProfileId?: string
  suggestedProfileName?: string
  /**
   * The profile name the file asked for, when this tenant has no profile of
   * that name. The choice is create it / point at an existing one / leave it
   * unset — never a hard error, or a customer would have to hand-create every
   * profile their spreadsheet mentions before importing anything.
   */
  missingProfileName?: string
}

export interface ImportPlan {
  columns: HeaderResolution[]
  rows: ImportRowPlan[]
  decisions: ImportProfileDecision[]
  summary: { rows: number; create: number; update: number; reusable: number; novel: number }
  errors: string[]
}

/** The caller's answer to one decision. */
export interface ImportDecision {
  profileType: InventoryProfileType
  valueSetKey: string
  choice: 'reuse' | 'create' | 'attributes'
  profileId?: string
  name?: string
}

export interface ImportRequest {
  headers: string[]
  rows: { cells: (string | number | boolean | null)[] }[]
  mapping?: {
    header: string
    kind: 'materialScalar' | 'profileName' | 'fieldValue' | 'ignore'
    profileType?: InventoryProfileType
    key?: string
  }[]
}

export interface ImportResult {
  created: number
  updated: number
  profilesCreated: number
  profilesReused: number
  count: number
}

/**
 * A refused import, carrying every problem the server found.
 *
 * The shared `request<T>` reads only `message` off a non-2xx body, so the whole
 * `errors` array — the entire point of an importer that collects failures
 * instead of stopping at the first — is lost without a client that keeps it.
 */
export class InventoryImportError extends Error {
  readonly errors: string[]
  readonly status: number

  constructor(message: string, errors: string[], status: number) {
    super(message)
    this.name = 'InventoryImportError'
    this.errors = errors
    this.status = status
  }
}

async function importRequest<T>(path: string, tenantId: string, payload: unknown): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json', 'X-Tenant-Id': tenantId }),
    body: JSON.stringify(payload),
  })
  handleUnauthorized(res.status)
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as {
      message?: string | string[]
      errors?: string[]
    } | null
    const errors = Array.isArray(body?.errors) ? body.errors : []
    const message = Array.isArray(body?.message)
      ? body.message.join('; ')
      : (body?.message ?? `Request failed: ${res.status}`)
    throw new InventoryImportError(message, errors, res.status)
  }
  return res.json() as Promise<T>
}

export const inventoryApi = {
  // ─── Materials ──────────────────────────────────────────────────────────

  listMaterials: (
    tenantId: string,
    params: {
      type?: string
      status?: string
      stock?: MaterialStockFilter
      q?: string
      page?: number
      pageSize?: number
      ids?: string[]
    } = {},
  ) => {
    const qs = new URLSearchParams()
    if (params.type) qs.set('type', params.type)
    // Only these materials, comma-separated (z8nrz7e8kw).
    if (params.ids?.length) qs.set('ids', params.ids.join(','))
    if (params.status) qs.set('status', params.status)
    if (params.stock) qs.set('stock', params.stock)
    if (params.q) qs.set('q', params.q)
    if (params.page) qs.set('page', String(params.page))
    if (params.pageSize) qs.set('pageSize', String(params.pageSize))
    const query = qs.toString()
    return request<MaterialListResult>(`/inventory/materials${query ? `?${query}` : ''}`, tenantId)
  },

  getMaterial: (tenantId: string, id: string) =>
    request<ApiMaterial>(`/inventory/materials/${id}`, tenantId),

  /**
   * Which lot a scanned label names, keyed on the material code and lot number
   * the code carries. A query param, not a path segment: the code contains a
   * `|`.
   *
   * The server re-parses the code rather than trusting the caller's, so a
   * malformed value rejects with 400 and an unknown one with 404 carrying
   * "No inventory lot matches this code." — the message the scan surface
   * renders.
   */
  resolveLotScan: (tenantId: string, code: string) =>
    request<ApiResolvedLot>(
      `/inventory/materials/lots/resolve?code=${encodeURIComponent(code)}`,
      tenantId,
    ),

  /**
   * The per-hold breakdown behind a lot's reserved quantity — who is holding it
   * and how much. Oldest hold first; empty when nothing is held.
   */
  listLotHolds: (tenantId: string, lotId: string) =>
    request<ApiLotHold[]>(`/inventory/materials/lots/${lotId}/holds`, tenantId),

  /**
   * Inventory-type procedures still holding material short of release — the
   * worklist behind Inventory → Pending Inventory. A plain array, not paginated.
   */
  listPending: (tenantId: string) => request<PendingRow[]>('/inventory/pending', tenantId),

  /**
   * Materials against their reorder point — the list behind Inventory →
   * Re-Order (z8nrz7f7dz). `counts` covers every filter, whichever one is
   * applied, so the filter buttons can show theirs without a second call.
   */
  listReorder: (
    tenantId: string,
    params: { filter?: ReorderFilter; type?: string; q?: string } = {},
  ) => {
    const qs = new URLSearchParams()
    if (params.filter) qs.set('filter', params.filter)
    if (params.type) qs.set('type', params.type)
    if (params.q) qs.set('q', params.q)
    const query = qs.toString()
    return request<ReorderListResult>(`/inventory/reorder${query ? `?${query}` : ''}`, tenantId)
  },

  /** Just the counts — what the Re-Order tab's label shows. */
  getReorderSummary: (tenantId: string) =>
    request<ReorderCounts>('/inventory/reorder/summary', tenantId),

  createMaterial: (tenantId: string, payload: CreateMaterialPayload) =>
    request<ApiMaterial>('/inventory/materials', tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateMaterial: (tenantId: string, id: string, payload: UpdateMaterialPayload) =>
    request<ApiMaterial>(`/inventory/materials/${id}`, tenantId, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  /**
   * Connect a material to the Inventory procedure TEMPLATE that gates its
   * lots, replacing any previous connection. Every lot received against the
   * material spawns its own run of this template; a run's gate status lives
   * on the lot (`ApiLot.gateStatus`), not the material. 400 if
   * `procedureTemplateId` is not an Inventory-typed template; 404 if the
   * material or template is unknown or belongs to another tenant.
   */
  connectProcedure: (tenantId: string, id: string, procedureTemplateId: string) =>
    request<ApiMaterial>(`/inventory/materials/${id}/procedure`, tenantId, {
      method: 'PUT',
      body: JSON.stringify({ procedureTemplateId }),
    }),

  /** Drop the connection. 400 if the material was not connected. */
  disconnectProcedure: (tenantId: string, id: string) =>
    request<ApiMaterial>(`/inventory/materials/${id}/procedure`, tenantId, {
      method: 'DELETE',
    }),

  addLot: (tenantId: string, materialId: string, payload: AddLotPayload) =>
    request<ApiLot>(`/inventory/materials/${materialId}/lots`, tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateLot: (tenantId: string, materialId: string, lotId: string, payload: UpdateLotPayload) =>
    request<ApiLot>(`/inventory/materials/${materialId}/lots/${lotId}`, tenantId, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  /**
   * Deleting a lot is irreversible — the row (and its own history) is gone —
   * so it is a signed, 21 CFR Part 11 act like starting or ending a process
   * step, not a plain delete. `signingToken` comes from
   * `authApi.createSigningToken` after the operator re-enters their password.
   */
  removeLot: (
    tenantId: string,
    materialId: string,
    lotId: string,
    payload: { signingToken: string; signatureMeaning: string; reason?: string },
  ) =>
    request<{ ok: true }>(`/inventory/materials/${materialId}/lots/${lotId}`, tenantId, {
      method: 'DELETE',
      body: JSON.stringify(payload),
    }),

  /**
   * `lotsRemoved` counts the cascade — deleting a material also deletes every
   * lot of it, emptying any storage slot those lots were in. Each of those
   * vacates is recorded on the location's own audit trail.
   */
  deleteMaterial: (tenantId: string, id: string) =>
    request<{ ok: true; lotsRemoved: number }>(`/inventory/materials/${id}`, tenantId, {
      method: 'DELETE',
    }),

  // ─── Profiles ───────────────────────────────────────────────────────────

  listProfiles: (tenantId: string, profileType?: InventoryProfileType) => {
    const qs = new URLSearchParams()
    if (profileType) qs.set('profileType', profileType)
    const query = qs.toString()
    return request<ApiProfile[]>(`/inventory/profiles${query ? `?${query}` : ''}`, tenantId)
  },

  getProfile: (tenantId: string, id: string) =>
    request<ApiProfile>(`/inventory/profiles/${id}`, tenantId),

  createProfile: (tenantId: string, payload: CreateProfilePayload) =>
    request<ApiProfile>('/inventory/profiles', tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateProfile: (tenantId: string, id: string, payload: UpdateProfilePayload) =>
    request<ApiProfile>(`/inventory/profiles/${id}`, tenantId, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  deleteProfile: (tenantId: string, id: string) =>
    request<{ ok: true }>(`/inventory/profiles/${id}`, tenantId, { method: 'DELETE' }),

  // ─── Field defs ─────────────────────────────────────────────────────────

  listFieldDefs: (tenantId: string, profileType?: InventoryProfileType) => {
    const qs = new URLSearchParams()
    if (profileType) qs.set('profileType', profileType)
    const query = qs.toString()
    return request<ApiFieldDef[]>(`/inventory/field-defs${query ? `?${query}` : ''}`, tenantId)
  },

  createFieldDef: (tenantId: string, payload: CreateFieldDefPayload) =>
    request<ApiFieldDef>('/inventory/field-defs', tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateFieldDef: (tenantId: string, id: string, payload: UpdateFieldDefPayload) =>
    request<ApiFieldDef>(`/inventory/field-defs/${id}`, tenantId, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  deleteFieldDef: (tenantId: string, id: string) =>
    request<{ ok: true }>(`/inventory/field-defs/${id}`, tenantId, { method: 'DELETE' }),

  // ─── Bulk import ────────────────────────────────────────────────────────

  getImportSchema: (tenantId: string) =>
    request<ImportSchema>('/inventory/import/schema', tenantId),

  previewImport: (tenantId: string, payload: ImportRequest) =>
    importRequest<ImportPlan>('/inventory/import/preview', tenantId, payload),

  commitImport: (tenantId: string, payload: ImportRequest & { decisions: ImportDecision[] }) =>
    importRequest<ImportResult>('/inventory/import/commit', tenantId, payload),
}
